import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import pathOr from 'ramda/src/pathOr';

import { SubMenu } from './Submenu';
import { RootState } from '../../../store/rootReducer';
import { makeMenuTree } from '../utils';
import { MenuTreeItem } from '../types';
import usePrevious from '../../common/hooks/usePrevious';
import { colors } from '../../../design-system';

export const MAX_NUMBER_OF_FIRST_LEVEL = 12;

const StyledMenu = styled.nav`
  .menu__list-container {
    margin: 1px 0;
    display: flex;
    overflow: hidden;
    height: 46px;
    flex-wrap: wrap;
    gap: 24px;
    padding: 0;
  }
  .menu__item {
    display: inline-block;
    list-style: none;
  }
  .menu__item-name {
    text-transform: uppercase;
    font-size: 1.4rem;
    line-height: 1.8rem;
    font-weight: 500;
    color: ${colors.WHITE};
    margin: 2px 0;
    display: block;
    padding: 12px 0;
  }
  .link-menu--disabled {
    cursor: inherit;
  }
`;

export const Menu = () => {
  const { key, pathname } = useLocation();

  const cmsContent = useSelector((state: RootState) => state.cms.menu);
  const items = pathOr([], ['items'], cmsContent);
  const tree = makeMenuTree(items);

  const [selected, setSelected] = useState<MenuTreeItem>();
  const [isOpen, setIsOpen] = useState(false);

  const closeSubmenu = () => {
    setSelected(undefined);
    setIsOpen(false);
  };

  const onMenuClick = () => {
    closeSubmenu();
  };

  const handleMouseEnter = (item: MenuTreeItem) => {
    const { columns = [], imageBlock = [] } = item;
    if (columns.length > 0 || imageBlock.length > 0) {
      setSelected(item);
      setIsOpen(true);
    } else {
      setSelected(item);
      setIsOpen(false);
    }
  };

  const prevKey = usePrevious(key);

  useEffect(() => {
    if (prevKey) {
      closeSubmenu();
    }
  }, [key]);

  return (
    <StyledMenu onMouseLeave={closeSubmenu}>
      <ul className="menu__list-container">
        {tree.slice(0, MAX_NUMBER_OF_FIRST_LEVEL).map((item, index) => {
          const isSelected = Boolean(
            selected?.name === item.name ||
              (item.link &&
                (pathname.startsWith(item.link) || pathname.startsWith(`/${item.link}`)))
          );
          return (
            <li key={`${item.name}-${index}`} className="menu__item">
              {item.link ? (
                <UnderlineLink
                  data-testid={`link-menu-${item.name}`}
                  aria-label={`Lien vers la page ${item.name}`}
                  className="link-menu"
                  onMouseEnter={() => handleMouseEnter(item)}
                  onClick={onMenuClick}
                  title={item.name}
                  to={item.link}
                  underline={isSelected ? 'true' : undefined}
                >
                  {item.name}
                </UnderlineLink>
              ) : (
                <span
                  onMouseEnter={() => handleMouseEnter(item)}
                  className="menu__item-name link-menu link-menu--disabled"
                >
                  {item.name}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <SubMenu isOpen={isOpen} selected={selected} />
    </StyledMenu>
  );
};

type UnderlineLinkProps = {
  underline: string | undefined;
};

const UnderlineLink = styled(Link)<UnderlineLinkProps>(
  {
    '::before': {
      display: 'block',
      content: 'attr(title)',
      height: 0,
      overflow: 'hidden',
      visibility: 'hidden',
    },
    textTransform: 'uppercase',
    fontSize: '1.2rem',
    lineHeight: '1.8rem',
    color: colors.WHITE,
    margin: '2px 0',
    display: 'block',
    padding: '12px 0',
  },
  ({ underline }) => {
    return (
      underline && {
        '&:after': {
          transform: 'scaleX(1)!important',
          transformOrigin: '0 100%!important',
        },
      }
    );
  }
);
